(function () {
  'use strict';
  if (customElements.get('rrg-cocky-preview')) return;

  function klaviyoPost(siteId, path, payload) {
    return fetch('https://a.klaviyo.com/client/' + path + '/?company_id=' + encodeURIComponent(siteId), {
      method: 'POST',
      headers: { 'Content-Type': 'application/vnd.api+json', Accept: 'application/vnd.api+json', revision: '2026-01-15' },
      body: JSON.stringify(payload)
    }).then(function (response) {
      if (!response.ok) throw new Error('Klaviyo request failed (' + response.status + ')');
      return response;
    });
  }

  class CockyPreview extends HTMLElement {
    connectedCallback() {
      this.cleanup = [];
      this.controller = new AbortController();
      this.setupFeathers();
      this.querySelectorAll('[data-countdown]').forEach(clock => this.setupCountdown(clock));
      this.querySelectorAll('[data-carousel]').forEach(carousel => this.setupCarousel(carousel));
      this.querySelectorAll('[data-cocky-form]').forEach(form => this.setupForm(form));
      this.querySelectorAll('.ccp__signup-link').forEach(link => this.listen(link, 'click', event => {
        const target = this.querySelector('[data-signup-heading]');
        if (!target) return;
        event.preventDefault();
        target.focus({ preventScroll: true });
        target.closest('.ccp__signup').scrollIntoView({ behavior: this.reducedMotion() ? 'auto' : 'smooth', block: 'start' });
      }));
    }

    disconnectedCallback() {
      this.controller.abort();
      this.cleanup.forEach(fn => fn());
    }

    listen(element, event, callback) {
      element.addEventListener(event, callback, { signal: this.controller.signal });
    }

    reducedMotion() { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; }

    setupFeathers() {
      const layer = this.querySelector('[data-feather-layer]');
      if (!layer || !Element.prototype.animate) return;
      const feathers = Array.from(layer.querySelectorAll('[data-feather]'));
      const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
      let animations = [];
      let visible = false;
      let dimensions = '';
      let collisionFrame;
      let lastTime = 0;
      const drift = feathers.map(() => ({ x: 0, velocity: 0 }));
      const separate = time => {
        const dt = Math.min((time - (lastTime || time)) / 1000, .04);
        lastTime = time;
        const bounds = feathers.map(feather => feather.querySelector('img').getBoundingClientRect());
        const forces = drift.map(state => -state.x * .12);
        bounds.forEach((a, i) => {
          if (a.bottom < 0 || a.top > innerHeight) return;
          for (let j = i + 1; j < bounds.length; j++) {
            const b = bounds[j];
            const dx = a.x + a.width / 2 - b.x - b.width / 2;
            const dy = a.y + a.height / 2 - b.y - b.height / 2;
            const distance = Math.hypot(dx / ((a.width + b.width) * .4 + 24), dy / ((a.height + b.height) * .4 + 24));
            if (distance >= 1) continue;
            const push = (dx === 0 ? (i % 2 ? 1 : -1) : Math.sign(dx)) * (1 - distance) * 260;
            forces[i] += push;
            forces[j] -= push;
          }
        });
        drift.forEach((state, i) => {
          state.velocity = Math.max(-55, Math.min(55, (state.velocity + forces[i] * dt) * Math.exp(-2 * dt)));
          state.x += state.velocity * dt;
          feathers[i].style.translate = state.x.toFixed(2) + 'px 0';
        });
        collisionFrame = requestAnimationFrame(separate);
      };
      const sync = () => {
        cancelAnimationFrame(collisionFrame);
        lastTime = 0;
        if (visible && !document.hidden && !preference.matches && animations.length) collisionFrame = requestAnimationFrame(separate);
        animations.forEach(animation => {
        if (visible && !document.hidden) animation.play();
        else animation.pause();
        });
      };
      const rebuild = () => {
        const progress = animations.filter((_, i) => i % 2 === 0).map(animation =>
          (animation.currentTime || 0) / animation.effect.getTiming().duration % 1);
        animations.forEach(animation => animation.cancel());
        animations = [];
        if (preference.matches) {
          drift.forEach((state, i) => { state.x = state.velocity = 0; feathers[i].style.removeProperty('translate'); });
          sync();
          return;
        }
        const height = layer.clientHeight;
        feathers.forEach((feather, index) => {
          const img = feather.querySelector('img');
          img.loading = 'eager';
          const style = getComputedStyle(feather);
          const y = parseFloat(style.top);
          const padding = feather.clientWidth * 2 + 100;
          const distance = height + padding * 2;
          const duration = distance / (20 + index % 5 * 4) * 1000;
          const fall = feather.animate([
            { transform: `translate3d(0,${-y - padding}px,0)` },
            { transform: `translate3d(0,${height - y + padding}px,0)` }
          ], { duration, iterations: Infinity, easing: 'linear' });
          fall.currentTime = (progress[index] ?? (y + padding) / distance) * duration;
          const angle = parseFloat(style.getPropertyValue('--rotate')) || 0;
          const flip = parseFloat(style.getPropertyValue('--flip')) || 1;
          const sway = Math.min(layer.clientWidth * .1, 55 + index % 4 * 18);
          const turn = 14 + index % 3 * 6;
          const breezeDuration = 12000 + index % 5 * 2000;
          const breezeEasing = 'cubic-bezier(0.45, 0, 0.55, 1)';
          const breeze = img.animate([
            { transform: `translateX(${-sway}px) rotate(${angle - turn}deg) scaleX(${flip})`, easing: breezeEasing },
            { transform: `translateX(${sway}px) rotate(${angle + turn}deg) scaleX(${flip})`, easing: breezeEasing },
            { transform: `translateX(${-sway}px) rotate(${angle - turn}deg) scaleX(${flip})` }
          ], { duration: breezeDuration, iterations: Infinity, easing: 'linear' });
          breeze.currentTime = index * 1937 % breezeDuration;
          animations.push(fall, breeze);
        });
        sync();
      };
      const observer = new IntersectionObserver(entries => {
        visible = entries[0].isIntersecting;
        sync();
      });
      observer.observe(this);
      const resize = new ResizeObserver(() => {
        const intro = this.querySelector('.ccp__intro');
        const top = intro ? intro.getBoundingClientRect().top - this.getBoundingClientRect().top : 0;
        layer.style.top = Math.max(0, top) + 'px';
        const next = layer.clientWidth + ':' + layer.clientHeight;
        if (next === dimensions) return;
        dimensions = next;
        rebuild();
      });
      resize.observe(layer);
      resize.observe(this.querySelector('.ccp__content'));
      this.listen(preference, 'change', rebuild);
      this.listen(document, 'visibilitychange', sync);
      this.cleanup.push(() => {
        observer.disconnect();
        resize.disconnect();
        cancelAnimationFrame(collisionFrame);
        feathers.forEach(feather => feather.style.removeProperty('translate'));
        animations.forEach(animation => animation.cancel());
      });
    }

    setupCountdown(clock) {
      const value = clock.querySelector('[data-countdown-value]');
      const description = clock.parentElement.querySelector('[data-countdown-description]');
      const deadline = clock.dataset.deadline;
      const validZone = /T\d{2}:\d{2}(:\d{2})?(Z|[+-]\d{2}:\d{2})$/.test(deadline);
      const end = validZone ? Date.parse(deadline) : NaN;
      if (!Number.isFinite(end)) {
        clock.hidden = true;
        if (description) description.textContent = 'Launching November 2, 2026';
        return;
      }
      clock.hidden = false;
      const update = () => {
        const remaining = Math.max(0, Math.ceil((end - Date.now()) / 1000));
        const days = Math.floor(remaining / 86400);
        const hours = Math.floor(remaining / 3600) % 24;
        const minutes = Math.floor(remaining / 60) % 60;
        value.textContent = [days, hours, minutes].map(n => String(n).padStart(2, '0')).join(' : ');
        clock.setAttribute('aria-label', days + ' days, ' + hours + ' hours, ' + minutes + ' minutes until launch');
        if (description) description.textContent = remaining > 0
          ? 'Launching ' + new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'America/New_York' }).format(end)
          : clock.dataset.expired;
        return remaining;
      };
      if (!update()) return;
      const timer = setInterval(() => { if (!update()) clearInterval(timer); }, 1000);
      this.cleanup.push(() => clearInterval(timer));
      this.listen(document, 'visibilitychange', () => { if (!document.hidden) update(); });
    }

    setupCarousel(carousel) {
      const track = carousel.querySelector('[data-track]');
      const slides = Array.from(track.querySelectorAll('[data-slide]'));
      const previous = carousel.querySelector('[data-previous]');
      const next = carousel.querySelector('[data-next]');
      const status = carousel.querySelector('[data-carousel-status]');
      const count = slides.length;
      if (count < 2) {
        previous.disabled = next.disabled = true;
        return;
      }
      const cloneSlide = slide => {
        const clone = slide.cloneNode(true);
        clone.removeAttribute('data-slide');
        clone.removeAttribute('data-shopify-editor-block');
        clone.removeAttribute('aria-label');
        clone.setAttribute('data-carousel-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        clone.inert = true;
        return clone;
      };
      track.prepend(...slides.map(cloneSlide));
      track.append(...slides.map(cloneSlide));
      let index = 0;
      let settle;
      let frame;
      let drag = null;
      const stride = () => slides[1].offsetLeft - slides[0].offsetLeft;
      const jump = position => {
        track.style.scrollSnapType = 'none';
        track.scrollTo({ left: position, behavior: 'instant' });
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => { track.style.scrollSnapType = ''; });
      };
      const normalize = () => {
        if (drag) return;
        const step = stride();
        if (!step) return;
        const absolute = Math.round(track.scrollLeft / step);
        index = ((absolute - count) % count + count) % count;
        if (absolute < count || absolute >= count * 2 || Math.abs(track.scrollLeft - absolute * step) > 1) jump((count + index) * step);
        status.textContent = 'Glove ' + (index + 1) + ' of ' + count + ': ' + slides[index].querySelector('img').alt;
      };
      const move = direction => {
        const step = stride();
        if (!step) return;
        track.scrollTo({ left: (Math.round(track.scrollLeft / step) + direction) * step, behavior: this.reducedMotion() ? 'instant' : 'smooth' });
      };
      this.listen(previous, 'click', () => move(-1));
      this.listen(next, 'click', () => move(1));
      this.listen(track, 'dragstart', event => event.preventDefault());
      this.listen(track, 'pointerdown', event => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        event.preventDefault();
        clearTimeout(settle);
        jump(track.scrollLeft);
        drag = { id: event.pointerId, x: event.clientX };
        track.setPointerCapture(event.pointerId);
        track.classList.add('is-dragging');
      });
      this.listen(track, 'pointermove', event => {
        if (!drag || drag.id !== event.pointerId) return;
        const cycle = count * stride();
        let position = track.scrollLeft + drag.x - event.clientX;
        if (cycle) {
          if (position < cycle) position += cycle;
          else if (position >= cycle * 2) position -= cycle;
        }
        track.scrollLeft = position;
        drag.x = event.clientX;
      });
      const endDrag = event => {
        if (!drag || drag.id !== event.pointerId) return;
        drag = null;
        track.classList.remove('is-dragging');
        if (track.hasPointerCapture(event.pointerId)) track.releasePointerCapture(event.pointerId);
        clearTimeout(settle);
        move(0);
        settle = setTimeout(normalize, 180);
      };
      this.listen(track, 'pointerup', endDrag);
      this.listen(track, 'pointercancel', endDrag);
      this.listen(track, 'lostpointercapture', endDrag);
      this.listen(track, 'keydown', event => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        move(event.key === 'ArrowRight' ? 1 : -1);
      });
      this.listen(track, 'scroll', () => { clearTimeout(settle); settle = setTimeout(normalize, 180); });
      const observer = new ResizeObserver(() => jump((count + index) * stride()));
      observer.observe(track);
      jump(count * stride());
      this.cleanup.push(() => {
        observer.disconnect();
        clearTimeout(settle);
        cancelAnimationFrame(frame);
        if (drag && track.hasPointerCapture(drag.id)) track.releasePointerCapture(drag.id);
        drag = null;
        track.classList.remove('is-dragging');
        track.querySelectorAll('[data-carousel-clone]').forEach(clone => clone.remove());
      });
      this.listen(document, 'shopify:block:select', event => {
        const slideIndex = slides.findIndex(slide => slide === event.target || slide.contains(event.target));
        if (slideIndex < 0) return;
        index = slideIndex;
        jump((count + index) * stride());
      });
    }

    setupForm(form) {
      const button = form.querySelector('[data-launch-submit]');
      const label = form.querySelector('[data-launch-submit-label]');
      const status = form.querySelector('[data-form-status]');
      const nameInput = form.elements.first_name;
      const emailInput = form.elements.email;
      let pending = false;
      button.disabled = false;
      const setStatus = (message, state) => {
        status.textContent = message;
        status.className = 'launch-launch-card__status' + (state ? ' is-' + state : '');
      };
      const syncReady = () => {
        form.classList.toggle('is-ready', !!(nameInput.value.trim() && emailInput.value.trim() && emailInput.checkValidity()));
      };
      [nameInput, emailInput].forEach(field => this.listen(field, 'input', () => {
        field.removeAttribute('aria-invalid');
        if (!pending) setStatus('', '');
        syncReady();
      }));
      const submit = async event => {
        event.preventDefault();
        event.stopPropagation();
        if (pending) return;
        const firstName = nameInput.value.trim();
        const email = emailInput.value.trim().toLowerCase();
        if (!firstName || !email || !emailInput.checkValidity()) {
          const invalid = !firstName ? nameInput : emailInput;
          invalid.setAttribute('aria-invalid', 'true');
          invalid.focus();
          setStatus('Please enter your name and a valid email address.', 'error');
          return;
        }
        const data = form.dataset;
        if (!data.siteId || !data.listId) {
          setStatus('Form unavailable right now. Please try again later.', 'error');
          return;
        }
        pending = true;
        button.disabled = true;
        form.setAttribute('aria-busy', 'true');
        label.textContent = 'Sending...';
        setStatus('Sending...', '');
        const profile = { email: email, first_name: firstName };
        try {
          await klaviyoPost(data.siteId, 'subscriptions', {
            data: {
              type: 'subscription',
              attributes: {
                custom_source: 'Shopify launch landing',
                profile: { data: { type: 'profile', attributes: {
                  ...profile,
                  properties: {
                    last_launch_key: data.launchKey, last_launch_name: data.launchName,
                    last_launch_date: data.launchDate, last_launch_date_iso: data.launchDateIso,
                    last_launch_month: data.launchMonth, last_launch_market: data.market
                  },
                  subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
                } } }
              },
              relationships: { list: { data: { type: 'list', id: data.listId } } }
            }
          });
          try {
            await klaviyoPost(data.siteId, 'events', {
              data: { type: 'event', attributes: {
                metric: { data: { type: 'metric', attributes: { name: data.eventName } } },
                profile: { data: { type: 'profile', attributes: profile } },
                properties: {
                  launch_key: data.launchKey, launch_name: data.launchName, launch_label: data.launchLabel,
                  launch_date: data.launchDate, launch_date_iso: data.launchDateIso,
                  month: data.launchMonth, market: data.market, page_url: window.location.href
                },
                unique_id: data.launchKey + '-' + Date.now()
              } }
            });
          } catch (error) { console.warn('Cocky Club signup saved; launch event could not be recorded.'); }
          form.reset();
          setStatus(data.successMessage, 'success');
        } catch (error) {
          setStatus('Could not save your request. Please try again.', 'error');
        } finally {
          pending = false;
          button.disabled = false;
          form.removeAttribute('aria-busy');
          label.textContent = 'Notify Me';
          syncReady();
        }
      };
      this.listen(button, 'click', submit);
      this.listen(form, 'submit', submit);
      syncReady();
    }
  }
  customElements.define('rrg-cocky-preview', CockyPreview);
}());
